import React, { useMemo } from "react";

export type OptimizedImageFormat = "webp" | "jpeg" | "png" | "avif";
export type OptimizedImageFit = "cover" | "contain" | "fill" | "inside" | "outside";

export interface OptimizedImageProps {
  /** The original S3 URL of the image to optimize */
  src: string;
  /** Target width in pixels (max 4000) */
  width?: number;
  /** Target height in pixels (max 4000) */
  height?: number;
  /** Compression quality 1-100 (default: 80) */
  quality?: number;
  /** Output format - webp, jpeg, png, avif */
  format?: OptimizedImageFormat;
  /** Resize behavior - cover, contain, fill, inside, outside (default: cover) */
  fit?: OptimizedImageFit;
  /**
   * When false, do not multiply width/height by window.devicePixelRatio. Default true.
   * Set false when an exact pixel size is required (e.g. server-side image requests).
   */
  applyDevicePixelRatio?: boolean;
  /**
   * Override the origin used for the image-service URL (defaults to window.location.origin).
   * Set this when the resulting URL must be fetchable by a remote service that cannot reach
   * the current origin (e.g. a localhost dev server).
   */
  originOverride?: string;
}

/**
 * Checks if the URL is an s3 url. This is a general detection mechanism and not narrowed
 * to Higharc specific buckets. This is deliberate so as not to share all of these bucket names in
 * source code. Also, this system supports fetching from buckets not associated with the deployment
 * environment, so this ensures that will work.
 */
function isS3Url(url: string) {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname;

    // Virtual-hosted style: bucket.s3.amazonaws.com or bucket.s3.region.amazonaws.com
    return /^.+\.s3[.-][a-z0-9-]+\.amazonaws\.com$/.test(host) || /^.+\.s3\.amazonaws\.com$/.test(host);
  } catch (e) {
    return false;
  }
}

function shouldUseImageService(): boolean {
  // PR environments don't run the image service; detect them from the hostname.
  if (typeof window !== "undefined" && window.location.hostname.endsWith(".pr.higharc.com")) {
    return false;
  }

  return true;
}

function getPrNumberFromHostname(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const match = window.location.hostname.match(/^(\d+)\.pr\.higharc\.com$/);
  return match?.[1] ?? null;
}

function addPrPrefixToS3UrlIfMissing(src: string): string {
  const prNumber = getPrNumberFromHostname();
  if (!prNumber) {
    return src;
  }

  try {
    const url = new URL(src);
    const isPrPublicBucketHost = /^product-pr\.s3[.-][a-z0-9-]+\.amazonaws\.com$/.test(url.hostname);
    if (!isPrPublicBucketHost) {
      return src;
    }

    const normalizedPath = url.pathname.replace(/^\/+/, "");
    if (!normalizedPath || normalizedPath.startsWith(`${prNumber}/`)) {
      return src;
    }

    url.pathname = `/${prNumber}/${normalizedPath}`;
    return url.toString();
  } catch {
    return src;
  }
}

/**
 * Helper function to build the optimized image URL with query parameters
 *
 * If the image src is not an s3 url (i.e. bucket.s3.amazonaws.com or bucket.s3.region.amazonaws.com),
 * the url is returned unmodified.
 */
export function buildOptimizedImageUrl({
  src,
  width,
  height,
  quality = 80,
  format,
  fit = "cover",
  applyDevicePixelRatio = true,
  originOverride,
}: OptimizedImageProps): string {
  if (!isS3Url(src)) {
    return src;
  }

  if (!shouldUseImageService()) {
    return addPrPrefixToS3UrlIfMissing(src);
  }

  const searchParams = new URLSearchParams();

  const devicePixelRatio = applyDevicePixelRatio && typeof window !== "undefined" ? window.devicePixelRatio : 1;

  searchParams.append("url", src);
  if (width) {
    searchParams.append("width", Math.floor(width * devicePixelRatio).toString());
  }
  if (height) {
    searchParams.append("height", Math.floor(height * devicePixelRatio).toString());
  }
  searchParams.append("quality", quality.toString());
  if (format) {
    searchParams.append("format", format);
  }
  searchParams.append("fit", fit);

  // Use the image-service endpoint
  const origin = originOverride ?? (typeof window !== "undefined" ? window.location.origin : "");
  const baseUrl = `${origin}/image-service`;

  return `${baseUrl}?${searchParams.toString()}`;
}

/**
 * OptimizedImage component that automatically optimizes images through the image-service endpoint.
 * Accepts optimization parameters and transforms the URL to request optimized versions.
 */
export default function OptimizedImage({
  src,
  width,
  height,
  quality = 80,
  format,
  fit = "cover",
  alt,
  ...imgProps
}: OptimizedImageProps & Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src">): React.ReactElement {
  const optimizedSrc = useMemo((): string => {
    return buildOptimizedImageUrl({ src, width, height, quality, format, fit });
  }, [src, width, height, quality, format, fit]);

  return <img src={optimizedSrc} alt={alt} {...imgProps} />;
}
